import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Pause, Play, X } from '@phosphor-icons/react';
import { getMediaUrl } from '../lib/media';
import { useSmartBack } from '../shared/navigation/useSmartBack';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Slideshow = () => {
  const { slug } = useParams();
  // iter197 — Use the canonical smart-back resolver so closing the
  // slideshow returns the guest to wherever they came from (Booth
  // OS, dashboard, or /access/:slug) instead of always /event/:slug.
  const { goBack: smartBack } = useSmartBack({ slug, fallbackHref: `/event/${slug}` });
  const [photos, setPhotos] = useState([]);
  const [event, setEvent] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);
  const SLIDE_DURATION = 4000;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const eventRes = await axios.get(`${API}/events/slug/${slug}`);
        setEvent(eventRes.data);
        const photosRes = await axios.get(`${API}/events/${eventRes.data.event_id}/photos`);
        // Filter to images only for slideshow
        const images = photosRes.data.filter(p => p.content_type?.startsWith('image/'));
        setPhotos(images);
      } catch (error) {
        console.error('Failed to load slideshow:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const advanceSlide = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % photos.length);
  }, [photos.length]);

  useEffect(() => {
    if (photos.length > 1 && !paused) {
      timerRef.current = setInterval(advanceSlide, SLIDE_DURATION);
      return () => clearInterval(timerRef.current);
    }
  }, [photos.length, paused, advanceSlide]);

  // Poll for new photos every 10s
  useEffect(() => {
    if (!event) return;
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/events/${event.event_id}/photos`);
        const images = res.data.filter(p => p.content_type?.startsWith('image/'));
        setPhotos(images);
      } catch (e) { /* ignore */ }
    }, 10000);
    return () => clearInterval(poll);
  }, [event]);

  const togglePause = () => setPaused(p => !p);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#B89762]/50 border-t-transparent"></div>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center text-center px-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">No Photos Yet</h1>
          <p className="text-gray-400 mb-6">Photos will appear here as guests upload them</p>
          <button onClick={smartBack} className="px-6 py-3 rounded-full bg-[#B89762] text-white font-semibold" data-testid="back-to-event">
            Back to Event
          </button>
        </div>
      </div>
    );
  }

  const currentPhoto = photos[currentIndex];

  return (
    <div className="fixed inset-0 bg-black" data-testid="slideshow-page">
      {/* Photo */}
      <div className="absolute inset-0 flex items-center justify-center">
        <img
          key={currentPhoto.photo_id}
          src={getMediaUrl(currentPhoto.storage_path, `${API}/photos/${currentPhoto.storage_path}`)}
          alt={currentPhoto.original_filename}
          className="max-w-full max-h-full object-contain slideshow-fade"
          data-testid="slideshow-image"
        />
      </div>

      {/* Overlay Controls */}
      <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center z-10 bg-gradient-to-b from-black/60 to-transparent">
        <div>
          <h2 className="text-xl font-bold text-white" data-testid="slideshow-event-name">
            {event?.event_name}
          </h2>
          <p className="text-white/60 text-sm">{currentIndex + 1} / {photos.length}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={togglePause}
            className="p-3 rounded-full bg-[#15161B]/10 backdrop-blur-sm hover:bg-[#15161B]/20 transition-colors text-white"
            data-testid="slideshow-pause-button"
          >
            {paused ? <Play size={20} weight="fill" /> : <Pause size={20} weight="fill" />}
          </button>
          <button
            onClick={smartBack}
            className="p-3 rounded-full bg-[#15161B]/10 backdrop-blur-sm hover:bg-[#15161B]/20 transition-colors text-white"
            data-testid="slideshow-close-button"
          >
            <X size={20} weight="bold" />
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#15161B]/10">
        <div
          className="h-full bg-[#B89762] transition-all"
          style={{
            width: `${((currentIndex + 1) / photos.length) * 100}%`,
            transition: 'width 0.5s ease-out'
          }}
        />
      </div>

      {/* Branding */}
      <div className="absolute bottom-4 right-4 text-white/30 text-xs font-semibold">
        EventDrop
      </div>

      <style>{`
        .slideshow-fade {
          animation: slideshowFadeIn 0.8s ease-out;
        }
        @keyframes slideshowFadeIn {
          from { opacity: 0; transform: scale(1.02); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default Slideshow;
